import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRegistrationDeadline } from "@/hooks/useRegistrationDeadline";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Users, UserPlus, Trophy, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { parseLocalDate } from "@/lib/utils";
import { format, addMonths } from "date-fns";

interface HeroState {
  type: "register_event" | "invite_friends" | "join_challenge" | "invite_teammates";
  title: string;
  subtitle: string;
  ctaLabel: string;
  ctaHref: string;
  icon: React.ElementType;
  eventName?: string;
  eventDate?: string;
  challengeName?: string;
  showFreeLabel?: boolean;
}

const DynamicHeroCTA: React.FC = () => {
  const { data: deadlineData } = useRegistrationDeadline();
  
  const { data: heroState, isLoading } = useQuery({
    queryKey: ["dashboard-hero-state"],
    staleTime: 30000,
    queryFn: async (): Promise<HeroState> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const today = new Date();
      const threeMonthsFromNow = addMonths(today, 3);

      // Check for upcoming events within 3 months
      const { data: upcomingEvents } = await supabase
        .from("events")
        .select("id, name, event_date, slug")
        .gte("event_date", today.toISOString().split("T")[0])
        .lte("event_date", threeMonthsFromNow.toISOString().split("T")[0])
        .order("event_date", { ascending: true })
        .limit(1);

      const upcomingEvent = upcomingEvents?.[0];

      if (upcomingEvent) {
        // Check if user is registered for this event
        const { data: registration } = await supabase
          .from("event_registrations")
          .select("id")
          .eq("user_id", user.id)
          .eq("event_id", upcomingEvent.id)
          .maybeSingle();

        const eventDate = parseLocalDate(upcomingEvent.event_date);
        const formattedDate = format(eventDate, "MMMM d, yyyy");

        if (!registration) {
          // Priority 1: Register for upcoming event
          return {
            type: "register_event",
            title: "Join Our Next Event",
            subtitle: `${upcomingEvent.name} on ${formattedDate}`,
            ctaLabel: "Register Now",
            ctaHref: `/events/${upcomingEvent.slug}`,
            icon: Calendar,
            eventName: upcomingEvent.name,
            eventDate: formattedDate,
            showFreeLabel: true,
          };
        }

        // User is registered - Priority 2: Invite friends to the event
        return {
          type: "invite_friends",
          title: "You're Registered!",
          subtitle: `Invite friends to join you at ${upcomingEvent.name}`,
          ctaLabel: "Invite Friends",
          ctaHref: "/dashboard/events",
          icon: UserPlus,
          eventName: upcomingEvent.name,
          eventDate: formattedDate,
        };
      }

      // No upcoming event - check if user is on a challenge team
      const { data: teamMemberships } = await supabase
        .from("challenge_team_members")
        .select(`
          team_id,
          role,
          challenge_teams (
            id,
            name,
            deleted_at
          )
        `)
        .eq("user_id", user.id);

      const activeTeams = teamMemberships?.filter(
        (m: any) => m.team_id && !m.challenge_teams?.deleted_at
      );

      if (activeTeams && activeTeams.length > 0) {
        // Check if any team has fewer than 3 members
        for (const team of activeTeams) {
          const { count: memberCount } = await supabase
            .from("challenge_team_members")
            .select("id", { count: "exact", head: true })
            .eq("team_id", team.team_id);

          if ((memberCount || 0) < 3) {
            // Priority 3: Invite teammates
            return {
              type: "invite_teammates",
              title: "Build Your Team",
              subtitle: "Invite teammates to strengthen your challenge team",
              ctaLabel: "Invite Teammates",
              ctaHref: "/dashboard/challenges",
              icon: Users,
            };
          }
        }

        // Team is complete - just show view challenges
        return {
          type: "join_challenge",
          title: "Find Your Next Challenge",
          subtitle: "Explore new challenges to work on with your team",
          ctaLabel: "Browse Challenges",
          ctaHref: "/challenges",
          icon: Trophy,
        };
      }

      // No team - Priority 4: Join a challenge
      return {
        type: "join_challenge",
        title: "Ready to Make an Impact?",
        subtitle: "Join a challenge and work with a team to solve real problems",
        ctaLabel: "Find a Challenge",
        ctaHref: "/challenges",
        icon: Trophy,
      };
    },
  });

  if (isLoading) {
    return (
      <Card className="mb-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-10 w-32" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!heroState) return null;

  const IconComponent = heroState.icon;

  return (
    <Card className="mb-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/20 overflow-hidden">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/20 text-primary shrink-0">
            <IconComponent className="h-6 w-6" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                {heroState.title}
              </h2>
              {heroState.type === "register_event" && (
                <Sparkles className="h-4 w-4 text-primary animate-pulse" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {heroState.subtitle}
              {heroState.showFreeLabel && deadlineData && !deadlineData.isExpired && (
                <span> • Free, limited space</span>
              )}
            </p>
          </div>
          
          <Button asChild size="lg" className="w-full sm:w-auto shrink-0">
            <Link to={heroState.ctaHref}>
              {heroState.ctaLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DynamicHeroCTA;
